import React, {useEffect} from 'react';
import {useAtom} from "jotai";
import {
  barActionAtom,
  barTitleAtom,
  currentFilterStepAtom,
  filterActive,
  filterReverseAtom,
  finishedAtom,
  termsBundleAtom
} from "../../atoms/atoms";
import TermsRender from "../Terms/TermsRender";
import { motion, AnimatePresence } from "framer-motion"
import colors from "../../styles/variables/colors";
import styled from "styled-components/macro";
import FixedFilter from "./FixedFilter";
import ActiveFilter from "../ActiveFilter/ActiveFilter";
import FilteredNodeButton from "../ActiveFilter/FilteredNodeButton";
import StatusIndicator from "../StatusIndicator/StatusIndicator";
import useWindowDimensions from "../../hooks/useWindowDimensions";

type FilterPageComponent = () => JSX.Element;


export const PillBackground = styled(motion.div)`
  padding: 24px 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  height: auto;

  @media (min-width: 1024px) {
    padding: 25px;
  }
`;

export const FilterActiveArea = styled(motion.div)`
  white-space: nowrap;
  overflow: hidden;
  background-color: ${colors.slate["10"]};
  border-radius: 15px 15px 0 0;
  display: inline;
  padding: 0 15px;
`;

export const PanelBackground = styled.div`
  background-color: ${colors.slate["90"]};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const Filters: FilterPageComponent = () => {
  const [active] = useAtom(filterActive);
  const [terms] = useAtom(termsBundleAtom);
  const [finished] = useAtom(finishedAtom);
  const [filterStep] = useAtom(currentFilterStepAtom);
  const [filterReverse] = useAtom(filterReverseAtom)
  const [,setBarTitle] = useAtom(barTitleAtom);
  const [,setBarAction] = useAtom(barActionAtom);
  const width = useWindowDimensions().width;

  const isDesktop = width && width >= 1024;

  useEffect(() => {
    if (active && !finished) {
      setBarTitle(terms[filterStep]['question'])
      setBarAction('Werbeformate finden')
    }
  }, [filterStep, active]);

  const gradientSelect = () => {
    switch (filterStep) {
      case 1:
        return colors.gradients.orange;
      case 2:
        return colors.gradients.blue;
      case 3:
        return colors.gradients.white;
      default:
        return colors.gradients.yellow;
    }
  }

  const calculateFilterHeight = () => {
    if (!active) {
      return 0;
    }
    if (finished) {
      return 0;
    }
    if (!isDesktop) {
      return 'calc(100vh - 100px)'
    }
    return '600px';
  }

  if (finished) {
    return <></>
  }

  return (
    <AnimatePresence>
      {(active && !finished) &&
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: calculateFilterHeight() }}
          exit={{ height: 0 }}
        >
          <>
            <div style={{ height: 4 }}>
              {filterStep > 0 ? (
                <StatusIndicator elements={3} type={'flat'}/>
              ) : (
                <StatusIndicator  elements={2} type={'flat'} />
              )}
            </div>
            <div style={{ height: '100%'}}>
              <PanelBackground style={{ background: gradientSelect()}}>
                <PillBackground
                  key={`filter_frame_${filterStep}`}
                  initial={{x: '10vw', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -600 }}
                  transition={{ duration: 0.3 }}
                >
                  <TermsRender terms={terms[filterStep]['terms']} />
                </PillBackground>
                {filterStep === 0 ? (
                  <FixedFilter />
                ) : (
                  <AnimatePresence>
                    {filterStep > 0 &&
                      <FilterActiveArea
                        initial={{ height: (filterStep > 1 || filterReverse ) ? "auto" : 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        transition={{ ease: "anticipate" }}
                      >
                        <ActiveFilter resultPage={false}/>
                        <FilteredNodeButton />
                      </FilterActiveArea>
                    }
                  </AnimatePresence>
                )}
              </PanelBackground>
            </div>
          </>
        </motion.div>
      }
    </AnimatePresence>
  );

}

export default Filters;
